import { ApiError, can, formatDate } from '@clim/client-core';
import {
  REVIEW_CHANNEL_LABELS,
  addDays,
  isoDate,
  type ReviewBatchResult,
  type ReviewCandidate,
  type ReviewChannel,
} from '@clim/domain';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Empty, ErrorBox, Field, Input, Modal, Select, Textarea } from '../components/ui';
import { errorMessage } from '../lib/errors';
import { useReady } from '../lib/session';
import { useTerritory } from '../lib/territory';
import { useOnline } from '../lib/use-online';

interface Settings {
  googleReviewUrl: string | null;
  message: string;
  territories: { id: string; name: string; googleReviewUrl: string | null }[];
}

/**
 * Avis clients (§16) : installations et entretiens terminés ; sélection d'un ou plusieurs clients, envoi du lien
 * Google Business (sans note préalable) par SMS, WhatsApp ou e-mail. Chaque envoi est daté ; un client déjà
 * sollicité demande une confirmation explicite.
 */
export function ReviewsPage() {
  const { api, me } = useReady();
  const online = useOnline();
  const { territoryId } = useTerritory();
  const today = isoDate(new Date());
  const [from, setFrom] = useState(addDays(today, -60));
  const [to, setTo] = useState(today);
  const [onlyNew, setOnlyNew] = useState(true);
  const [rows, setRows] = useState<ReviewCandidate[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => {
    if (!online) return;
    const q = new URLSearchParams({ from, to, ...(territoryId ? { territoryId } : {}) });
    api.request<ReviewCandidate[]>('GET', `/v1/reviews/candidates?${q}`).then(
      (r) => (setRows(r), setError(null)),
      (e) => setError(errorMessage(e)),
    );
  }, [api, online, from, to, territoryId]);
  useEffect(load, [load]);
  const list = (rows ?? []).filter((r) => !onlyNew || !r.requests.length);
  const toggle = (id: string) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setSelected(s);
  };
  const missingUrl = list.some((r) => !r.reviewUrl);
  return (
    <div>
      <div className="page-head">
        <h1>Avis clients</h1>
        {can(me, 'tenant.settings.manage') && (
          <button className="btn small" onClick={() => setSettingsOpen(true)}>
            Lien Google et message
          </button>
        )}
      </div>
      <p className="muted">
        Installations et entretiens terminés. Le client reçoit directement votre lien Google : aucune note
        n’est demandée avant.
      </p>
      <div className="filters">
        <Field label="Terminés du">
          <Input type="date" value={from} onChange={(e) => e.target.value && setFrom(e.target.value)} />
        </Field>
        <Field label="au">
          <Input type="date" value={to} onChange={(e) => e.target.value && setTo(e.target.value)} />
        </Field>
        <label className="check">
          <input type="checkbox" checked={onlyNew} onChange={(e) => setOnlyNew(e.target.checked)} /> Seulement
          les clients jamais sollicités
        </label>
      </div>
      {!online && <Empty>L’envoi des demandes d’avis nécessite une connexion.</Empty>}
      <ErrorBox message={error} />
      {missingUrl && (
        <p className="warn">
          Lien Google Business non configuré pour certains territoires : renseignez-le dans « Lien Google et
          message » ou dans les paramètres du territoire.
        </p>
      )}
      {selected.size > 0 && (
        <div className="card selection-bar">
          <strong>{selected.size} client(s) sélectionné(s)</strong>
          <button className="btn primary small" onClick={() => setSending(true)}>
            Envoyer la demande d’avis
          </button>
        </div>
      )}
      {rows && !list.length && (
        <Empty>
          {onlyNew
            ? 'Tous les clients de la période ont déjà été sollicités.'
            : 'Aucune intervention terminée sur la période.'}
        </Empty>
      )}
      {list.length > 0 && (
        <table className="table" aria-label="Installations terminées">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  aria-label="Tout sélectionner"
                  checked={list.every((r) => selected.has(r.dossierId))}
                  onChange={(e) =>
                    setSelected(e.target.checked ? new Set(list.map((r) => r.dossierId)) : new Set())
                  }
                />
              </th>
              <th>Client</th>
              <th>Intervention</th>
              <th>Demandes d’avis</th>
            </tr>
          </thead>
          <tbody>
            {list.map((r) => (
              <tr key={r.dossierId}>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Sélectionner ${r.clientName}`}
                    checked={selected.has(r.dossierId)}
                    onChange={() => toggle(r.dossierId)}
                  />
                </td>
                <td>
                  <strong>{r.clientName}</strong>
                  <div className="muted">{[r.phone, r.email, r.city].filter(Boolean).join(' · ')}</div>
                </td>
                <td>
                  <Link to={`/dossiers/${r.dossierId}`}>{r.reference}</Link> · {r.typeLabel}
                  <div className="muted">
                    terminé le {formatDate(r.completedAt)}
                    {r.technicianName ? ` · ${r.technicianName}` : ''}
                  </div>
                </td>
                <td>
                  {r.requests.length ? (
                    r.requests.map((q) => (
                      <div key={q.id} className="muted">
                        {REVIEW_CHANNEL_LABELS[q.channel]} · {formatDate(q.at)} · {q.byName}
                      </div>
                    ))
                  ) : (
                    <span className="badge tone-info">Jamais sollicité</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {sending && (
        <SendDialog
          rows={(rows ?? []).filter((r) => selected.has(r.dossierId))}
          onClose={() => {
            setSending(false);
            setSelected(new Set());
            load();
          }}
        />
      )}
      {settingsOpen && <SettingsDialog onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}

function SendDialog({ rows, onClose }: { rows: ReviewCandidate[]; onClose(): void }) {
  const { api } = useReady();
  const [sent, setSent] = useState<Record<string, string>>({});
  const [confirm, setConfirm] = useState<{
    row: ReviewCandidate;
    channel: ReviewChannel;
    message: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [auto, setAuto] = useState(false);
  const [relaunch, setRelaunch] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<Record<string, ReviewBatchResult>>({});
  useEffect(() => {
    api.request<{ enabled: boolean }>('GET', '/v1/messaging/status').then(
      (s) => setAuto(s.enabled),
      () => undefined,
    );
  }, [api]);
  const already = rows.filter((r) => r.requests.length).length;
  const sendAll = async (channel: 'sms' | 'email') => {
    setError(null);
    setBusy(true);
    try {
      const out = await api.request<ReviewBatchResult[]>('POST', '/v1/reviews/requests/batch', {
        dossierIds: rows.map((r) => r.dossierId),
        channel,
        force: relaunch,
      });
      setResults(Object.fromEntries(out.map((r) => [r.dossierId, r])));
      setSent({
        ...sent,
        ...Object.fromEntries(
          out.filter((r) => r.ok).map((r) => [r.dossierId, `${REVIEW_CHANNEL_LABELS[channel]}, automatique`]),
        ),
      });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const send = async (row: ReviewCandidate, channel: ReviewChannel, force = false) => {
    setError(null);
    // the window is opened right away (browsers block pop-ups opened after an await)
    const win = channel === 'whatsapp' ? window.open('about:blank', '_blank') : null;
    try {
      const r = await api.request<{ href: string }>('POST', '/v1/reviews/requests', {
        dossierId: row.dossierId,
        channel,
        force,
      });
      setSent({ ...sent, [row.dossierId]: REVIEW_CHANNEL_LABELS[channel] });
      if (win) win.location.href = r.href;
      else window.location.href = r.href;
    } catch (e) {
      win?.close();
      if (e instanceof ApiError && e.status === 409 && /déjà envoyée/.test(e.message))
        setConfirm({ row, channel, message: e.message });
      else setError(errorMessage(e));
    }
  };
  return (
    <Modal title="Envoyer la demande d’avis" onClose={onClose}>
      {auto && (
        <div className="card">
          <h3>Envoi automatique</h3>
          <p className="muted">
            Le message part directement (SMS ou e-mail), sans ouvrir votre téléphone. Chaque envoi est
            enregistré avec la date.
          </p>
          {already > 0 && (
            <label className="check">
              <input type="checkbox" checked={relaunch} onChange={(e) => setRelaunch(e.target.checked)} />{' '}
              Relancer aussi les {already} client(s) déjà sollicité(s)
            </label>
          )}
          <div className="actions">
            <button className="btn primary small" disabled={busy} onClick={() => void sendAll('sms')}>
              Tout envoyer par SMS ({rows.length})
            </button>
            <button className="btn small" disabled={busy} onClick={() => void sendAll('email')}>
              Tout envoyer par e-mail ({rows.length})
            </button>
          </div>
        </div>
      )}
      <p className="muted">
        {auto ? 'Ou, client par client' : 'Pour chaque client, choisissez le canal'} : le message (avec votre
        lien Google) s’ouvre prêt à envoyer. L’envoi est enregistré avec la date.
      </p>
      <ErrorBox message={error} />
      <ul className="plain">
        {rows.map((r) => (
          <li key={r.dossierId} className="row send-row">
            <span className="row-main">
              <strong>{r.clientName}</strong>
              <span className="muted">
                {r.requests.length
                  ? `Déjà sollicité le ${formatDate(r.requests[0]!.at)}`
                  : 'Jamais sollicité'}
                {sent[r.dossierId] ? ` · envoyé (${sent[r.dossierId]}) ✓` : ''}
                {results[r.dossierId] && !results[r.dossierId]!.ok
                  ? ` · non envoyé : ${results[r.dossierId]!.error}`
                  : ''}
              </span>
            </span>
            <span className="actions">
              {(['sms', 'whatsapp', 'email'] as ReviewChannel[]).map((c) => (
                <button
                  key={c}
                  className="btn small"
                  disabled={c === 'email' ? !r.email : !r.phone}
                  onClick={() => void send(r, c)}
                  aria-label={`${REVIEW_CHANNEL_LABELS[c]} à ${r.clientName}`}
                >
                  {REVIEW_CHANNEL_LABELS[c]}
                </button>
              ))}
            </span>
          </li>
        ))}
      </ul>
      <button className="btn" onClick={onClose}>
        Terminé
      </button>
      {confirm && (
        <Modal title="Client déjà sollicité" onClose={() => setConfirm(null)}>
          <p>{confirm.message}.</p>
          <p className="muted">Pour éviter les relances involontaires, un second envoi doit être confirmé.</p>
          <div className="actions">
            <button className="btn" onClick={() => setConfirm(null)}>
              Ne pas renvoyer
            </button>
            <button
              className="btn primary"
              onClick={() => {
                const c = confirm;
                setConfirm(null);
                void send(c.row, c.channel, true);
              }}
            >
              Renvoyer quand même
            </button>
          </div>
        </Modal>
      )}
    </Modal>
  );
}

function SettingsDialog({ onClose }: { onClose(): void }) {
  const { api } = useReady();
  const [s, setS] = useState<Settings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [terr, setTerr] = useState('');
  useEffect(() => {
    api.request<Settings>('GET', '/v1/reviews/settings').then(setS, (e) => setError(errorMessage(e)));
  }, [api]);
  const save = async () => {
    if (!s) return;
    try {
      await api.request('PUT', '/v1/reviews/settings', {
        googleReviewUrl: s.googleReviewUrl || null,
        message: s.message,
      });
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  return (
    <Modal title="Lien Google et message" onClose={onClose}>
      {s && (
        <div className="form">
          <Field
            label="Lien Google Business de l’entreprise"
            hint="Utilisé quand le territoire n’a pas son propre lien (paramètres du territoire)."
          >
            <Input
              value={s.googleReviewUrl ?? ''}
              onChange={(e) => setS({ ...s, googleReviewUrl: e.target.value })}
              placeholder="https://g.page/r/…/review"
            />
          </Field>
          <Field label="Message" hint="Variables : {prenom}, {entreprise}, {lien} (obligatoire).">
            <Textarea rows={4} value={s.message} onChange={(e) => setS({ ...s, message: e.target.value })} />
          </Field>
          <Field label="Liens par territoire">
            <Select value={terr} onChange={(e) => setTerr(e.target.value)}>
              <option value="">Voir…</option>
              {s.territories.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} : {t.googleReviewUrl ?? 'lien de l’entreprise'}
                </option>
              ))}
            </Select>
          </Field>
          <ErrorBox message={error} />
          <button className="btn primary" onClick={() => void save()}>
            Enregistrer
          </button>
        </div>
      )}
    </Modal>
  );
}
